import React, { useState } from "react";
import {
    Button,
    ButtonProps,
    useNotify,
    useRecordContext,
} from "react-admin";
import { GiArchiveResearch } from "react-icons/gi";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    CircularProgress,
    Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { clientNoJson } from "../../dataProvider";
import { url as URL } from "../../constants";
import { format } from "date-fns";
import { it } from "date-fns/locale";


type HistoryChange = {
    field: string;
    label: string;
    old: any;
    new: any;
};

type ChargeRecord = {
    id: number;
    delivery: string | null;
    quantity: number;
    price: number;
    taxable: number;
    causal: string | null;
    notes: string | null;
    client_id: number | null;
    supplier_id: number | null;
    tariff_id: number | null;
    invoice_line_id: number | null;
    charge_date: string | null;
    not_to_be_invoiced: boolean;
    verified: boolean;
    main_charge: string | null;
};


const columnOrder: (keyof ChargeRecord)[] = [
    "id",
    "delivery",
    "main_charge",
    "quantity",
    "price",
    "taxable",
    "causal",
    "notes",
    "client_id",
    "supplier_id",
    "tariff_id",
    "invoice_line_id",
    "charge_date",
    "verified",
    "not_to_be_invoiced",
];

type HistoryRecord = {
    charge_id: number;
    delivery_id: number;
    history_type: string;
    history_date: string;
    history_user: string | null;
    current_values: {
        quantity: number;
        price: number;
        taxable: number;
        causal: string | null;
        notes: string | null;
        client_id: number | null;
        supplier_id: number | null;
        tariff_id: number | null;
        invoice_line_id: number | null;
        charge_date: string | null;
        not_to_be_invoiced: boolean;
        verified: boolean;
        delivery: string | null;
        main_charge: string | null;
    };
    changes: HistoryChange[];
};


export const DeliveryChargeLogButton = (
    props: ButtonProps
) => {
    const record = useRecordContext();
    const notify = useNotify();

    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [history, setHistory] = useState<HistoryRecord[]>([]);
    const [charges, setCharges] = useState<ChargeRecord[]>([]);

    const handleOpen = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!record?.id) {
            notify("Nessuna delivery selezionata", "warning");
            return;
        }

        setOpen(true);
        setLoading(true);

        try {
            const res = await clientNoJson(
                `${URL}/deliveries/${record.id}/get_charge_history/`,
                {
                    method: "GET",
                }
            );

            // supponiamo che res.data sia l'array JSON
            setHistory(res.data.logs as HistoryRecord[]);
            setCharges(res.data.charges as ChargeRecord[]);
        } catch (err: any) {
            console.error(err);
            // se il backend restituisce errori "normali" JSON:
            if (err?.response?.data?.detail) {
                notify(err.response.data.detail, "error");
            } else {
                notify("Errore nel recupero dello storico addebiti", "error");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setOpen(false);
    };

    const formatDate = (dateStr?: string | null) => {
        if (!dateStr) return "-";
        try {
            return format(new Date(dateStr), "dd/MM/yyyy HH:mm", { locale: it });
        } catch {
            return dateStr;
        }
    };

    return (
        <>
            <Button {...props} label="" onClick={handleOpen} title={"Addebiti missione + log"}>
                <>
                    <GiArchiveResearch size={25}/>
                </>
            </Button>

            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle disableTypography style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography variant="h6">
                        Addebiti delivery #{record?.id}
                    </Typography>
                    <IconButton onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    {loading && (
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                padding: "2rem",
                            }}
                        >
                            <CircularProgress />
                        </div>
                    )}

                    {!loading && charges.length === 0 && (
                        <Typography>Nessuno addebito correlato disponibile.</Typography>
                    )}

                    {!loading && charges.length > 0 && (
                        <table
                            style={{
                                width: "100%",
                                borderCollapse: "collapse",
                                marginTop: "1rem",
                            }}
                        >
                            <thead>
                            <tr>
                                {columnOrder.map((key) => (
                                    <th
                                        key={key}
                                        style={{
                                            borderBottom: "2px solid #ddd",
                                            padding: "8px 12px",
                                            textAlign: "left",
                                            backgroundColor: "#f4f4f4",
                                            fontWeight: "bold",
                                        }}
                                    >
                                        {key}
                                    </th>
                                ))}
                            </tr>
                            </thead>

                            <tbody>
                            {charges.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                    {columnOrder.map((key, colIndex) => (
                                        <td
                                            key={colIndex}
                                            style={{
                                                padding: "8px 12px",
                                                borderBottom: "1px solid #eee",
                                            }}
                                        >
                                            {row[key] === "deleted" ? (
                                                <span style={{ color: "red", fontWeight: "bold" }}>
                                                    {String(row[key])}
                                                </span>
                                            ) : (
                                                String(row[key])
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    )}


                    <hr/>

                    {!loading && history.length === 0 ? (
                        <Typography>Nessuno storico disponibile.</Typography>
                    ) :
                        <Typography variant={"h6"}>Storico modifche addebiti</Typography>

                    }

                    {!loading &&
                        history.map((h, index) => (
                            <div
                                key={`${h.charge_id}-${index}`}
                                style={{
                                    marginBottom: "1.5rem",
                                    paddingBottom: "1rem",
                                    borderBottom: "1px solid #eee",
                                }}
                            >
                                <Typography variant="subtitle2" gutterBottom>
                                    <strong>Charge ID:</strong> {h.charge_id} |{" "}
                                    <strong>Tipo:</strong> {h.history_type} |{" "}
                                    <strong>Data:</strong> {formatDate(h.history_date)} |{" "}
                                    <strong>Utente:</strong>{" "}
                                    {h.history_user ?? "N/D"}
                                </Typography>

                                {h.changes.length > 0 ? (
                                    <ul style={{ marginTop: 4 }}>
                                        {h.changes.map((c, idx) => (
                                            <li key={idx}>
                                                <strong>{c.label}:</strong>{" "}
                                                <span style={{ textDecoration: "line-through" }}>
                                                    {String(c.old)}
                                                </span>{" "}
                                                → <span>{String(c.new)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (<>
                                    {h.history_type === "Creato" ? (
                                        <ul style={{ marginTop: 4 }}>
                                            {Object.entries(h.current_values).map(([key, value], idx) => (
                                                <li key={idx}>
                                                    <strong>{key}:</strong> {value === "deleted" ? <p style={{display: "inline", color:"red"}}>{String(value)}</p>: String(value)}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <Typography variant="body2" color="textSecondary">
                                            Nessun dettaglio variazione (record senza diff).
                                        </Typography>
                                    )}
                                    </>)
                                }
                            </div>
                        ))}
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleClose} color="primary"><Typography>Chiudi</Typography></Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
